import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useParties } from "./PartyContext";
import { Party, PartyType } from "@/types/party";

export type ClientType = "Customer" | "Vendor" | "Supplier";

export interface ClientTransaction {
  id: string;
  date: string;
  type: "Invoice" | "Payment" | "Receipt" | "Credit Note" | "Debit Note" | "Purchase";
  reference: string;
  amount: number;
  balance: number;
  status: "Completed" | "Pending" | "Overdue";
}

export interface Client {
  id: string;
  type: ClientType;
  name: string;
  gstin: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  creditLimit: number;
  outstandingBalance: number;
  totalTransactions: number;
  status: "Active" | "Inactive";
  transactions: ClientTransaction[];
  createdAt: string;
  updatedAt: string;
}

function partyToClient(p: Party): Client {
  const typeMap: Record<PartyType, ClientType> = {
    customer: "Customer",
    supplier: "Supplier",
    both: "Customer",
  };

  return {
    id: p.id,
    type: typeMap[p.partyType] || "Customer",
    name: p.name,
    gstin: p.gstin || "",
    phone: p.phone || "",
    email: p.email || "",
    address: p.billingAddress || "",
    city: p.billingCity || "",
    state: p.billingState || "",
    pincode: p.billingPincode || "",
    creditLimit: p.creditLimit || 0,
    outstandingBalance: p.currentBalance || 0,
    totalTransactions: 0,
    status: p.status === "active" ? "Active" : "Inactive",
    transactions: [],
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  };
}

interface ClientContextType {
  clients: Client[];
  loading: boolean;
  addClient: (data: Omit<Client, "id" | "createdAt" | "updatedAt" | "totalTransactions" | "transactions">) => Promise<Client>;
  updateClient: (id: string, data: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  getClientsByType: (type: ClientType) => Client[];
  getClient: (id: string) => Client | undefined;
}

const ClientContext = createContext<ClientContextType | null>(null);

export function ClientProvider({ children }: { children: ReactNode }) {
  const { parties, loading, createParty, updateParty, archiveParty } = useParties();
  const clients = parties.map(partyToClient);

  const addClient = useCallback(
    async (data: Omit<Client, "id" | "createdAt" | "updatedAt" | "totalTransactions" | "transactions">) => {
      const partyType: PartyType = (data.type === "Supplier" || data.type === "Vendor") ? "supplier" : "customer";
      const { party } = await createParty({
        name: data.name,
        partyType,
        gstRegistered: !!data.gstin && data.gstin !== "URP",
        gstin: data.gstin !== "URP" ? data.gstin : undefined,
        phone: data.phone,
        email: data.email,
        billingAddress: data.address,
        billingCity: data.city,
        billingState: data.state,
        billingPincode: data.pincode,
        creditLimit: data.creditLimit,
        openingBalance: data.outstandingBalance,
      });

      return partyToClient(party);
    },
    [createParty]
  );

  const handleUpdateClient = useCallback(
    async (id: string, data: Partial<Client>) => {
      await updateParty(id, {
        name: data.name,
        gstin: data.gstin,
        phone: data.phone,
        email: data.email,
        billingAddress: data.address,
        billingCity: data.city,
        billingState: data.state,
        billingPincode: data.pincode,
        creditLimit: data.creditLimit,
      });
    },
    [updateParty]
  );

  const handleDeleteClient = useCallback(
    async (id: string) => {
      await archiveParty(id);
    },
    [archiveParty]
  );

  const getClientsByType = useCallback(
    (type: ClientType) => clients.filter((c) => c.type === type),
    [clients]
  );

  const getClient = useCallback(
    (id: string) => clients.find((c) => c.id === id),
    [clients]
  );

  return (
    <ClientContext.Provider
      value={{
        clients,
        loading,
        addClient,
        updateClient: handleUpdateClient,
        deleteClient: handleDeleteClient,
        getClientsByType,
        getClient,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export function useClients() {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error("useClients must be used within ClientProvider");
  return ctx;
}
